// import React, { useState } from "react";
// import { useNavigate, useSearchParams } from "react-router-dom";
// import GlobalCrossoverMomentum from "../Screeners/GlobalCrossoverMomentum";
// import "./ScreenerPage.css";

// const SCREENER_TABS = [
//   {
//     key: "nasdaq100",
//     label: "NASDAQ 100",
//     shortDesc: "MACD crossover universe",
//     component: GlobalCrossoverMomentum,
//     props: { universe: "NASDAQ100" },
//   },
//   {
//     key: "sp500",
//     label: "S&P 500",
//     shortDesc: "MACD crossover universe",
//     component: GlobalCrossoverMomentum,
//     props: { universe: "SP500" },
//   },
//   // easy to add later: global gainers, global smart money, etc.
// ];

// const GlobalScreenerPage = () => {
//   const navigate = useNavigate();
//   const [searchParams, setSearchParams] = useSearchParams();

//   // ── initial tab comes from ?tab=nasdaq100 (default) ──
//   const initial = searchParams.get("tab") || "nasdaq100";
//   const [activeKey, setActiveKey] = useState(
//     SCREENER_TABS.some((t) => t.key === initial) ? initial : "nasdaq100",
//   );

//   const handleTabChange = (key) => {
//     setActiveKey(key);
//     setSearchParams({ tab: key }, { replace: true });
//   };

//   const activeTab = SCREENER_TABS.find((t) => t.key === activeKey);
//   const ActiveComponent = activeTab.component;

//   return (
//     <div className="scrn">
//       <div className="scrn__tab-bar">
//         <button className="scrn__back-btn" onClick={() => navigate(-1)}>
//           ←
//         </button>

//         <div className="scrn__tab-group">
//           {SCREENER_TABS.map((tab) => {
//             const isActive = tab.key === activeKey;
//             return (
//               <button
//                 key={tab.key}
//                 className={`scrn__tab ${isActive ? "scrn__tab--active" : ""}`}
//                 data-tab={tab.key}
//                 onClick={() => handleTabChange(tab.key)}
//               >
//                 <span className="scrn__tab-label">{tab.label}</span>
//                 <span className="scrn__tab-desc">{tab.shortDesc}</span>
//               </button>
//             );
//           })}
//         </div>
//       </div>

//       <div className="scrn__content">
//         <ActiveComponent key={activeKey} {...(activeTab.props || {})} />
//       </div>
//     </div>
//   );
// };

// export default GlobalScreenerPage;

import React, { useState, useMemo, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import GlobalCrossoverMomentum from "../Screeners/GlobalCrossoverMomentum";
import "./ScreenerPage.css";

// ═══════════════════════════════════════════════════════════════
//  CROSSOVER TAB WRAPPER
//  Renders GlobalCrossoverMomentum with a NASDAQ 100 ↔ S&P 500
//  toggle above it. Same visual pattern as the NSE Daily/Weekly/
//  Monthly pill row on the NSE Crossover tab.
//  Default universe = NASDAQ 100.
// ═══════════════════════════════════════════════════════════════
const CrossoverWithUniverseToggle = ({
  initialUniverse = "NASDAQ100",
  onUniverseChange,
}) => {
  const [universe, setUniverse] = useState(initialUniverse);

  const handleToggle = (u) => {
    if (u === universe) return;
    setUniverse(u);
    onUniverseChange?.(u);
  };

  return (
    <div className="gscrn__crossover-wrap">
      {/* Universe toggle — sits above the crossover header */}
      <div className="gscrn__uni-toggle">
        <button
          type="button"
          className={`gscrn__uni-btn ${
            universe === "NASDAQ100" ? "gscrn__uni-btn--active" : ""
          }`}
          onClick={() => handleToggle("NASDAQ100")}
        >
          NASDAQ 100
        </button>
        <button
          type="button"
          className={`gscrn__uni-btn ${
            universe === "SP500" ? "gscrn__uni-btn--active" : ""
          }`}
          onClick={() => handleToggle("SP500")}
        >
          S&P 500
        </button>
      </div>

      {/* key remounts the screener on universe change → clean state / fresh fetch */}
      <GlobalCrossoverMomentum key={universe} universe={universe} />
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
//  TAB CONFIG — mirrors ScreenerPage.jsx shape (label + shortDesc)
//  Add more global screeners here as their components ship.
// ═══════════════════════════════════════════════════════════════
const SCREENER_TABS = [
  {
    key: "crossover",
    label: "Crossover",
    shortDesc: "MACD signals · NASDAQ 100 & S&P 500",
    component: CrossoverWithUniverseToggle,
  },
  // Example of how to add more later — each screener gets its own
  // small component so this shell stays flat and additive:
  //
  // {
  //   key: "gainers",
  //   label: "Top Gainers",
  //   shortDesc: "Highest daily gains",
  //   component: GlobalGainersScreener,
  // },
];

// ═══════════════════════════════════════════════════════════════
//  URL → INITIAL STATE
//  Supported query params:
//    ?tab=crossover                    → Crossover tab, NASDAQ default
//    ?tab=crossover&universe=sp500     → Crossover tab, S&P 500
//    ?tab=nasdaq100   (legacy)         → Crossover tab, NASDAQ
//    ?tab=sp500       (legacy)         → Crossover tab, S&P 500
//  The two legacy shapes come from MarketScreener's handleTabChange,
//  which still redirects here when the user clicks the NASDAQ100 /
//  SP500 pills inside the market screener.
// ═══════════════════════════════════════════════════════════════
const parseInitialState = (searchParams) => {
  const rawTab = searchParams.get("tab");
  const rawUni = (searchParams.get("universe") || "").toLowerCase();

  if (rawTab === "nasdaq100")
    return { tab: "crossover", universe: "NASDAQ100" };
  if (rawTab === "sp500") return { tab: "crossover", universe: "SP500" };

  const tab = SCREENER_TABS.some((t) => t.key === rawTab)
    ? rawTab
    : SCREENER_TABS[0].key;
  const universe = rawUni === "sp500" ? "SP500" : "NASDAQ100";

  return { tab, universe };
};

// ═══════════════════════════════════════════════════════════════
//  MAIN PAGE
// ═══════════════════════════════════════════════════════════════
const GlobalScreenerPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Read once on mount so subsequent URL updates don't fight state.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const initial = useMemo(() => parseInitialState(searchParams), []);

  const [activeKey, setActiveKey] = useState(initial.tab);
  const [universe, setUniverse] = useState(initial.universe);

  const handleTabChange = useCallback(
    (key) => {
      setActiveKey(key);
      const params = { tab: key };
      if (key === "crossover") params.universe = universe.toLowerCase();
      setSearchParams(params, { replace: true });
    },
    [universe, setSearchParams],
  );

  const handleUniverseChange = useCallback(
    (u) => {
      setUniverse(u);
      setSearchParams(
        { tab: "crossover", universe: u.toLowerCase() },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const activeTab = SCREENER_TABS.find((t) => t.key === activeKey);
  const ActiveComponent = activeTab.component;

  // Only the crossover tab needs universe props — kept generic so
  // future tabs don't have to accept them.
  const activeProps =
    activeTab.key === "crossover"
      ? { initialUniverse: universe, onUniverseChange: handleUniverseChange }
      : {};

  return (
    <div className="scrn">
      {/* ── Sticky tab bar — same shell as NSE ScreenerPage ── */}
      <div className="scrn__tab-bar">
        <button className="scrn__back-btn" onClick={() => navigate(-1)}>
          ←
        </button>

        <div className="scrn__tab-group">
          {SCREENER_TABS.map((tab) => {
            const isActive = tab.key === activeKey;
            return (
              <button
                key={tab.key}
                type="button"
                className={`scrn__tab ${isActive ? "scrn__tab--active" : ""}`}
                data-tab={tab.key}
                onClick={() => handleTabChange(tab.key)}
              >
                <span className="scrn__tab-label">{tab.label}</span>
                <span className="scrn__tab-desc">{tab.shortDesc}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Active screener body ── */}
      <div className="scrn__content">
        {/* key on activeKey resets the tab's internal state on switch */}
        <ActiveComponent key={activeKey} {...activeProps} />
      </div>
    </div>
  );
};

export default GlobalScreenerPage;
